import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import { hexToRGB } from 'src/components/Marketplace/helpers';

export const useStyles = makeStyles((theme: Theme) => createStyles({
  badges: {
    display: 'flex',
    gap: 8,
    padding: '16px 16px 0 16px',
    flexWrap: 'wrap',
    minHeight: '48px',
  },
  badge: {
    backgroundColor: 'rgba(30, 30, 30, 0.05)',
    borderRadius: 50,
    padding: '6px 12px',
    height: '32px',
    fontSize: theme.typography.fontSizes?.small,
    lineHeight: 1,
    fontWeight: 400,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    whiteSpace: 'nowrap',

    [theme.breakpoints.up(3840)]: {
      fontSize: 18,
    },
  },
  active: {
    background: '#1E1E1E',
    color: '#FFF',

    '& img': {
      filter: 'invert(1)',
    },
  },
  activeRed: {
    background: '#ED1C24',
    color: '#FFF',
    '& img': {
      filter: 'invert(1)',
    },
  },
  badgeImage: {
    width: '16px',
    height: '16px',
  },
  cardImage: {
    padding: '16px',
    borderBottom: `1px solid ${hexToRGB(
      theme.typography.userInterfaceElements?.divider.default.colorStyle.color,
      theme.typography.userInterfaceElements?.divider.default.colorStyle.opacity,
    )}`,

    [theme.breakpoints.down('md')]: {
      padding: '10px 16px',
    },
  },
  planImageWrapper: {
    height: '288px',
    cursor: 'pointer',
    [theme.breakpoints.down('md')]: {
      height: '238px',
    },

    [theme.breakpoints.up(3840)]: {
      height: '400px',
    },
  },
  planImageWrapperGridCols6: {
    height: '220px',
  },
  planImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    display: 'block',
    transition: 'transform 0.3s ease',
  },
  info: {
    padding: '16px 16px 0px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 8,
  },
  priceRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  oldPrice: {
    fontSize: theme.typography.fontSizes?.small,
    fontWeight: '400',
    color: 'rgba(30, 30, 30, 0.7)',
    [theme.breakpoints.down('md')]: {
      fontSize: 12,
    },
  },
  newPrice: {
    fontWeight: 500,
    fontSize: theme.typography.fontSizes?.large,
    lineHeight: 1,
    color: '#1E1E1E',

    [theme.breakpoints.down('md')]: {
      fontSize: 18,
    },

    [theme.breakpoints.up(3840)]: {
      fontSize: theme.typography.fontSizes?.extraLarge,
    },
  },

  flatRow: {
    display: 'flex',
    gap: 4,
  },

  flatItem: {
    height: '32px',
    display: 'flex',
    fontSize: theme.typography.fontSizes?.small,
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px 12px',
    border: '1px solid rgba(30, 30, 30, 0.3)',
    borderRadius: 8,
    lineHeight: 1,

    [theme.breakpoints.up(3840)]: {
      fontSize: 18,
      height: '36px',
    },
  },

  flatSale: {
    background: '#ED1C24',
    color: '#fff',
    border: 'none',
  },

  desc: {
    padding: '0 16px',
    marginBottom: 16,
  },

  title: {
    display: 'flex',
    gap: 8,
    fontSize: theme.typography.fontSizes?.large,
    fontWeight: 500,
    lineHeight: '32px',
    color: '#1E1E1E',
    margin: '8px 0',

    [theme.breakpoints.down('md')]: {
      fontSize: 18,
    },

    [theme.breakpoints.up(3840)]: {
      fontSize: theme.typography.fontSizes?.extraLarge,
    },
  },

  featuresItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: theme.typography.fontSizes?.small,

    [theme.breakpoints.down('md')]: {
      fontSize: 12,
    },

    [theme.breakpoints.up(3840)]: {
      fontSize: 18,
    },

    '& span': {
      display: '-webkit-box',
      '-webkit-box-orient': 'vertical',
      '-webkit-line-clamp': 1,
      overflow: 'hidden',
    },
  },

  featureIcon: {
    width: '20px',
    height: '20px',
  },

  additionalInfo: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',

    '& span': {
      fontWeight: 400,
      fontSize: theme.typography.fontSizes?.small,
      lineHeight: '16px',
      color: 'rgba(30, 30, 30, 0.7)',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',

      [theme.breakpoints.down('md')]: {
        fontSize: 12,
      },
    },
    '& .divider': {
      height: '16px',
      width: '5px',
    },
  },

  features: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px 16px',
    fontWeight: 400,
    fontSize: theme.typography.fontSizes?.small,
    lineHeight: '16px',
    color: 'rgba(30, 30, 30, 0.7)',
    marginBottom: '8px',

    [theme.breakpoints.down('md')]: {
      gap: '8px 12px',
    },

    '&:last-child': {
      marginBottom: 0,
    },
  },

  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderTop: `1px solid ${hexToRGB(
      theme.typography.userInterfaceElements?.divider.default.colorStyle.color,
      theme.typography.userInterfaceElements?.divider.default.colorStyle.opacity,
    )}`,
    padding: 16,
    marginTop: 'auto',

    [theme.breakpoints.up(3840)]: {
      padding: 20,
    },
    '& img': {
      height: '36px',
      maxWidth: '152px',

      [theme.breakpoints.up(3840)]: {
        height: '48px',
        maxWidth: '200px',
      },
    },
  },
  footerItem: {
    display: 'block',
  },
  projectTitle: {
    fontWeight: 400,
    fontSize: theme.typography.fontSizes?.small,
    lineHeight: '16px',
    color: '#1E1E1E',

    [theme.breakpoints.up(3840)]: {
      fontSize: 18,
    },
  },
  address: {
    fontWeight: 400,
    fontSize: theme.typography.fontSizes?.small,
    lineHeight: '16px',
    color: 'rgba(30, 30, 30, 0.7)',
    marginTop: 4,
    display: '-webkit-box',
    overflow: 'hidden',
    '-webkit-box-orient': 'vertical',
    '-webkit-line-clamp': 1,

    [theme.breakpoints.up(3840)]: {
      fontSize: 18,
      marginTop: 8,
    },
  },
  footerHover: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 9,
    height: 68,
    background: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    padding: 16,
    transform: 'translateY(100%)',
    transition: 'transform 0.3s ease',

    [theme.breakpoints.up(3840)]: {
      padding: 20,
      height: 88,
    },
  },
  footerHoverItemImg: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '36px',
    height: '36px',
    background: `${hexToRGB(
      theme.typography.button?.quickAction.background.default.colorStyle.color,
      theme.typography.button?.quickAction.background.default.colorStyle.opacity,
    )} !important`,
    border: `1px solid ${hexToRGB(
      theme.typography.button?.quickAction.border.default.colorStyle.color,
      theme.typography.button?.quickAction.border.default.colorStyle.opacity,
    )} !important`,
    borderRadius: theme.typography.button?.quickAction.cornerRadius,

    [theme.breakpoints.up(2560)]: {
      width: '44px',
      height: '44px',
    },

    '&:hover': {
      border: `1px solid ${hexToRGB(
        theme.typography.button?.quickAction.border.hover.colorStyle.color,
        theme.typography.button?.quickAction.border.hover.colorStyle.opacity,
      )} !important`,
      background: `${hexToRGB(
        theme.typography.button?.quickAction.background.hover.colorStyle.color,
        theme.typography.button?.quickAction.background.hover.colorStyle.opacity,
      )} !important`,
    },

    '& .quick-action-btn': {
      display: 'inline-block',
      width: '100%',
      height: '100%',
      cursor: 'pointer!important',
    },

    [theme.breakpoints.up(3840)]: {
      width: '48px',
      height: '48px',
    },
  },
  footerHoverBtn: {
    fontWeight: 500,
    fontSize: theme.typography.fontSizes?.medium,
    lineHeight: '20px',
    borderBottom: `1px solid ${theme.typography.button?.link.text.default.colorStyle.color} !important`,
    color: theme.typography.button?.link.text.default.colorStyle.color,
    opacity: theme.typography.button?.link.text.default.colorStyle.opacity,
    cursor: 'pointer',

    [theme.breakpoints.up(3840)]: {
      fontSize: theme.typography.fontSizes?.large,
    },

    '&:hover': {
      color: theme.typography.button?.link.text.hover.colorStyle.color,
      opacity: theme.typography.button?.link.text.hover.colorStyle.opacity,
    },
  },
  footerHoverList: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,

    '& li': {
      '& span': {
        display: 'none',
        fontSize: theme.typography.fontSizes?.small,
        fontWeight: 400,
      },

      '&:only-child': {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',

        '& span': {
          display: 'block',
        },
      },
    },
  },
  card: {
    border: `1px solid ${hexToRGB(
      theme.typography.userInterfaceElements?.outline.default.colorStyle.color,
      theme.typography.userInterfaceElements?.outline.default.colorStyle.opacity,
    )}`,
    borderRadius: 8,
    backgroundColor: `${hexToRGB(
      theme.typography.background?.interface.colorStyle.color,
      theme.typography.background?.interface.colorStyle.opacity,
    )}`,
    position: 'relative',
    overflow: 'hidden',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'border-color 0.3s ease',

    [theme.breakpoints.down('md')]: {
      '& $footerHover': {
        transform: 'translateY(0%)',
        transition: 'transform 0.3s ease',
      },
    },

    [theme.breakpoints.up('md')]: {
      '&:hover': {
        borderColor: `${hexToRGB(
          theme.typography.userInterfaceElements?.outline.hover.colorStyle.color,
          theme.typography.userInterfaceElements?.outline.hover.colorStyle.opacity,
        )}`,
        transition: 'border-color 0.3s ease',

        '& $footerHover': {
          transform: 'translateY(0%)',
          transition: 'transform 0.3s ease',
        },

        '& $planImage': {
          transform: 'scale(1.05)',
          transition: 'transform 0.3s ease',
        },
      },
    },
  },
  gridCols4ItemHover: {
    display: 'flex',
  },
  gridCols6ItemHover: {
    display: 'block',

    '& $footer img': {
      display: 'none',
    },

    '& $title': {
      fontSize: '24px',
      flexWrap: 'wrap',

      [theme.breakpoints.up(3840)]: {
        fontSize: theme.typography.fontSizes?.extraLarge,
        marginTop: 12,
      },
    },
    '& $newPrice': {
      fontSize: '24px',
      [theme.breakpoints.up(3840)]: {
        fontSize: theme.typography.fontSizes?.extraLarge,
      },
    },
    '& $oldPrice': {
      fontSize: '16px',
    },
    '& $planImageWrapper': {
      height: '196px',

      [theme.breakpoints.up(3840)]: {
        height: '360px',
      },
    },
    '& $cardImage': {
      padding: '12px 16px',
    },

    '& $features': {
      display: 'none',
    },
  },
  gridCols8ItemHover: {
    display: 'block',
  },
}));
